import React, { useEffect, useState, useRef } from "react";
import { Icon } from "design-system/components/icon/icon";
import { LoadingSpinner } from "components/icons/loading-spinner";
import { PracticeLeaderboardProps } from "./practice-leaderboard.types";
import {
  PracticeLeaderboardWrapper,
  LeaderboardTitle,
  LeaderboardFilter,
  FilterTab,
  LoadingContainer,
  LeaderboardInfo,
  InfoContainer,
  InfoTitle,
  InfoDuration,
  DurationTimer,
  TimerSeparator,
  Divider,
  InfoText,
  LeaderboardPosition,
  PositionRank,
  RankLabel,
  RankNumber,
  PositionDesc,
  LeaderboardList,
  ListStudent,
  Student,
  StudentRank,
  Rank,
  StudentNameContainer,
  StudentName,
  StudentBadge,
  StudentPoint,
  LeaderboardDivider,
  LeaderboardEmpty,
  EmptyImage,
  EmptyText,
  TextTitle,
  TextDesc,
  InfoGradeContainer
} from "./practice-leaderboard.styles";
import { Button } from "design-system/components/button/button";
import { ButtonSize } from "design-system/components/button/button.types";
import EmptyLeaderboard from "public/assets-v2/illustrations/leaderboard_empty.svg";
import { trackPracticeRankedViewed } from "services/api/app-events/practiceEvents";

export const PracticeLeaderboard: React.FC<PracticeLeaderboardProps> = ({
  accessToken,
  weeklyData,
  semesterlyData
}) => {
  const [activeTab, setActiveTab] = useState<"weekly" | "semesterly">("weekly");
  const [showAll, setShowAll] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const leaderboardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const leaderboardData = activeTab === "weekly" ? weeklyData : semesterlyData;

  const getTimeRemaining = (endDate: string, now: number) => {
    const end = new Date(endDate);
    const totalMinutes = Math.max(
      0,
      Math.floor((end.getTime() - now) / 1000 / 60)
    );
    const days = Math.floor(totalMinutes / 60 / 24);
    const hours = Math.floor(totalMinutes / 60) % 24;
    const mins = totalMinutes % 60;

    return { days, hours, mins };
  };

  const time = leaderboardData
    ? getTimeRemaining(leaderboardData.end_date, currentTime)
    : null;

  const getSemesterLabel = (startDate: string) => {
    const month = new Date(startDate).getMonth();
    const year = new Date(startDate).getFullYear();
    const semester = month >= 6 ? "Semester 1" : "Semester 2";
    const academicStartYear = month >= 6 ? year : year - 1;
    return `${semester} ${academicStartYear}/${academicStartYear + 1}`;
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });

  const getGrade = (grade: number) => {
    if (grade <= 6) {
      return `SD Kelas ${grade}`;
    } else if (grade <= 9) {
      return `SMP Kelas ${grade}`;
    } else {
      return `SMA Kelas ${grade}`;
    }
  };

  const getPositionMessage = (position: number | null, activeTab: string) => {
    if (position === null) {
      return "Selesaikan latihanmu, nanti muncul di peringkat.";
    } else if (position >= 1 && position <= 10) {
      return "Kamu masuk top 10 minggu ini! Pertahankan terus ya";
    } else {
      if (activeTab === "weekly") {
        return "Keren banget, kamu udah selesaikan latihan minggu ini!";
      } else {
        return "Kamu udah ada di peringkat! Rajin latihan, posisimu pasti naik";
      }
    }
  };

  const visibleStudents = leaderboardData?.leaderboard.slice(
    0,
    showAll ? leaderboardData.leaderboard.length : 10
  );

  let content;
  if (!leaderboardData) {
    content = (
      <LoadingContainer>
        <LoadingSpinner variant="ring" />
      </LoadingContainer>
    );
  } else {
    content = (
      <>
        <LeaderboardInfo>
          <InfoContainer>
            <InfoTitle>
              {activeTab === "weekly"
                ? "Waktu peringkat berakhir"
                : getSemesterLabel(leaderboardData?.start_date ?? "")}
            </InfoTitle>
            <InfoDuration>
              {activeTab === "weekly" ? (
                <>
                  <DurationTimer>{time?.days}h</DurationTimer>
                  <TimerSeparator>:</TimerSeparator>
                  <DurationTimer>{time?.hours}j</DurationTimer>
                  <TimerSeparator>:</TimerSeparator>
                  <DurationTimer>{time?.mins}m</DurationTimer>
                </>
              ) : (
                <InfoText>
                  Berakhir {formatDate(leaderboardData?.end_date ?? "")}{" "}
                </InfoText>
              )}
            </InfoDuration>
          </InfoContainer>

          <Divider></Divider>

          <InfoGradeContainer>
            <InfoTitle>Kelasmu</InfoTitle>
            <InfoText>{getGrade(leaderboardData?.grade ?? 0)}</InfoText>
          </InfoGradeContainer>
        </LeaderboardInfo>

        <LeaderboardPosition
          $isActive={
            leaderboardData !== null &&
            leaderboardData.my_position !== null &&
            leaderboardData.my_position <= 10
          }
        >
          <PositionRank>
            <RankLabel>Posisi</RankLabel>
            <RankNumber>{leaderboardData?.my_position ?? "-"}</RankNumber>
          </PositionRank>
          <PositionDesc>
            {getPositionMessage(
              leaderboardData?.my_position ?? null,
              activeTab
            )}
          </PositionDesc>
        </LeaderboardPosition>

        {leaderboardData && leaderboardData.leaderboard.length > 0 ? (
          <LeaderboardList>
            <ListStudent>
              {visibleStudents?.map((student, index) => (
                <React.Fragment key={index}>
                  <Student
                    $isActive={index + 1 === leaderboardData.my_position}
                    $isTop10={index + 1 <= 10}
                  >
                    <StudentRank>
                      <Rank>{index + 1}</Rank>
                    </StudentRank>
                    <StudentNameContainer>
                      <StudentName>{student.name}</StudentName>
                      {index + 1 === leaderboardData.my_position && (
                        <StudentBadge>Kamu</StudentBadge>
                      )}
                    </StudentNameContainer>
                    <StudentPoint>{student.point} Poin</StudentPoint>
                  </Student>
                  {index !== visibleStudents.length - 1 &&
                    index + 1 !== leaderboardData.my_position &&
                    index + 2 !== leaderboardData.my_position && (
                      <LeaderboardDivider />
                    )}
                </React.Fragment>
              ))}
            </ListStudent>
            {leaderboardData.leaderboard.length > 10 && (
              <Button
                appearance="general"
                variant="ghost"
                onClick={() => {
                  if (showAll) {
                    const wrapper = leaderboardRef.current;
                    const isScrollable =
                      wrapper && wrapper.scrollHeight > wrapper.clientHeight;
                    if (isScrollable) {
                      wrapper.scrollTo({ top: 0, behavior: "smooth" });
                    } else {
                      wrapper?.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                  if (!showAll && leaderboardData) {
                    const position = leaderboardData.my_position;
                    trackPracticeRankedViewed(
                      accessToken,
                      activeTab,
                      position !== null && position <= 10
                    );
                  }
                  setShowAll(!showAll);
                }}
                size={ButtonSize.sm}
              >
                {showAll ? "Kembali ke Atas" : "Lihat Peringkat Lainnya"}
                {showAll ? (
                  <Icon name="navigation-chevron-up" />
                ) : (
                  <Icon name="navigation-chevron-down" />
                )}
              </Button>
            )}
          </LeaderboardList>
        ) : (
          <LeaderboardEmpty>
            <EmptyImage src={EmptyLeaderboard} alt="" />
            <EmptyText>
              <TextTitle>
                Jadi yang pertama buat
                <br />
                muncul di peringkat
              </TextTitle>
              <TextDesc>Kelasmu belum ada yang mulai latihan, nih</TextDesc>
            </EmptyText>
          </LeaderboardEmpty>
        )}
      </>
    );
  }

  return (
    <PracticeLeaderboardWrapper ref={leaderboardRef}>
      <LeaderboardTitle>Peringkat Latihan 🏆</LeaderboardTitle>
      <LeaderboardFilter>
        <FilterTab
          $isActive={activeTab === "weekly"}
          onClick={() => setActiveTab("weekly")}
        >
          Mingguan
        </FilterTab>
        <FilterTab
          $isActive={activeTab === "semesterly"}
          onClick={() => setActiveTab("semesterly")}
        >
          Semester
        </FilterTab>
      </LeaderboardFilter>
      {content}
    </PracticeLeaderboardWrapper>
  );
};
